import { useRef, useState } from 'react';
import type { SalonSettings } from '@/lib/salonSettings';
import { addGalleryPhoto, MAX_PHOTO_BYTES } from '@/lib/galleryStore';

// Галерея работ: владелец заменяет фото, правит подпись, добавляет и удаляет карточки.
// В production business.gallery хранит URL публичных файлов Pages.
// Демо: фото читается в data-URL и живёт в localStorage, без сети.

export function GalleryEditor({
  settings,
  onChange
}: {
  settings: SalonSettings;
  onChange: (gallery: SalonSettings['gallery']) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const gallery = settings.gallery;

  const updateCaption = (src: string, next: string) => {
    onChange(gallery.map((g) => (g.src === src ? { ...g, caption: next } : g)));
  };

  const remove = (src: string) => {
    onChange(gallery.filter((g) => g.src !== src));
  };

  const replace = async (src: string, file: File) => {
    setErr(null);
    if (file.size > MAX_PHOTO_BYTES) {
      setErr('Фото больше 4 МБ — сожмите его перед загрузкой.');
      return;
    }
    setBusy(true);
    try {
      const item = await addGalleryPhoto(file, caption || 'Работа');
      onChange(gallery.map((g) => (g.src === src ? { ...g, src: item.src } : g)));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось загрузить фото.');
    } finally {
      setBusy(false);
    }
  };

  const add = async (file: File) => {
    setErr(null);
    setBusy(true);
    try {
      const item = await addGalleryPhoto(file, caption);
      onChange([...gallery, item]);
      setCaption('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось загрузить фото.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mnc-step">
      <h2>
        <span className="mnc-step-num">Ф</span>
        Галерея работ
      </h2>
      <p className="mnc-muted">Фото видны на витрине. Нажмите на снимок, чтобы заменить его.</p>

      <div className="mnc-gallery">
        {gallery.map((g) => (
          <figure className="mnc-shot" key={g.src}>
            <img src={g.src} alt={g.caption} />
            <figcaption>
              <input
                className="mnc-caption-input"
                value={g.caption}
                onChange={(e) => updateCaption(g.src, e.target.value)}
                placeholder="Подпись"
              />
            </figcaption>
            <label className="mnc-shot-actions">
              <span className="mnc-linklike">Заменить</span>
              <input
                type="file"
                accept="image/*"
                hidden
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void replace(g.src, f);
                  e.target.value = '';
                }}
              />
            </label>
            <button
              className="mnc-shot-remove"
              aria-label="Удалить фото"
              onClick={() => remove(g.src)}
            >
              ✕
            </button>
          </figure>
        ))}
      </div>

      <label className="mnc-field">
        Подпись к новому фото
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Например: Гель-лак, нюд"
        />
      </label>
      <div className="mnc-actions">
        <button
          className="mnc-btn mnc-btn-ghost"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? 'Загружаем…' : 'Добавить фото'}
        </button>
        <input
          ref={fileRef}
          type="file"
          aria-label="Выбрать новое фото для галереи"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void add(f);
            e.target.value = '';
          }}
        />
      </div>
      {err && <p className="mnc-muted">{err}</p>}
    </section>
  );
}
